import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import EnrollmentFields from "./EnrollmentFields";
import { DocumentPreviewPage } from "./DirectDocumentEditor";
import { documentConfigs, ENROLLMENT_YEAR } from "./documentConfig";
import { loadEnrollmentDocument, saveEnrollmentDocument } from "./enrollmentStorage";
import { mapSolicitudToAnexoValues, anexoGuardianFieldIds } from "./anexoPrefillValues";
import { enrollmentDateFieldIds, resolveSolicitudValues } from "./SolicitudInscripcion";
import { contractStudentFieldIds } from "./contractStudentValues";
import "./documentEditor.css";
import "./enrollmentFields.css";
import "./documentacionCompleta.css";

const hiddenSolicitudFields = new Set([...contractStudentFieldIds, ...enrollmentDateFieldIds]);
const hiddenAnexoFields = new Set([
  ...anexoGuardianFieldIds,
  "student_dni",
  "student_name_first_line",
]);

const fieldsFor = (config, values) =>
  Object.fromEntries(
    config.fields.map(({ id }) => [id, values[id]]).filter(([, value]) => value !== undefined)
  );

export default function DocumentacionCompleta({
  renderPreview,
  exportDocuments,
  backHref = "/formularios",
  versionLabel = "",
}) {
  const [values, setValues] = useState({});
  const [step, setStep] = useState("form");
  const [busy, setBusy] = useState("");
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    window.document.title = `Documentación completa ${ENROLLMENT_YEAR} | Sagrado Corazón`;
  }, []);

  useEffect(() => {
    if (step !== "preview") return;

    window.scrollTo({
      top: 0,
      behavior: window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
    });
  }, [step]);

  const solicitudValues = useMemo(() => resolveSolicitudValues(values), [values]);
  const anexoValues = useMemo(
    () => ({ ...values, ...mapSolicitudToAnexoValues(solicitudValues, values.student_dni) }),
    [values, solicitudValues]
  );

  const updateField = (field, value) => {
    setStatus("");
    setValues((current) => {
      const next = { ...current, [field.id]: value };
      if (field.type === "checkbox" && field.group && value) {
        [...documentConfigs.solicitud.fields, ...documentConfigs.anexo.fields].forEach(
          (candidate) => {
            if (candidate.id !== field.id && candidate.group === field.group)
              next[candidate.id] = false;
          }
        );
      }
      return next;
    });
  };

  const loadByDni = async (rawDni) => {
    const dni = String(rawDni || "").trim();
    if (!dni || busy) return;
    setBusy("lookup");
    setError("");
    try {
      const [solicitud, anexo] = await Promise.all([
        loadEnrollmentDocument(dni, "solicitud"),
        loadEnrollmentDocument(dni, "anexo"),
      ]);
      setValues((current) => ({ ...current, ...solicitud, ...anexo, student_dni: dni }));
      setStatus(
        Object.keys(solicitud).length || Object.keys(anexo).length ? "loaded" : "not-found"
      );
    } catch (lookupError) {
      console.error(lookupError);
      setError(
        "No se pudieron buscar los datos guardados. Podés continuar completando el formulario."
      );
    } finally {
      setBusy("");
    }
  };

  const buildPdf = async (type) => {
    const { buildCompletedDocumentPdf, convertPdfToA4 } = await import(
      "../../utils/generateCompletedDocumentPdf"
    );
    const config = documentConfigs[type];
    const source = new Uint8Array(await (await fetch(config.pdfUrl)).arrayBuffer());
    const data = type === "solicitud" ? solicitudValues : anexoValues;
    const completed = await buildCompletedDocumentPdf(source, config, data);
    return type === "solicitud" ? convertPdfToA4(completed) : completed;
  };

  const download = async (type) => {
    setBusy(type);
    setError("");
    try {
      const dni = String(values.student_dni || "").trim();
      if (dni) {
        try {
          await Promise.all([
            saveEnrollmentDocument(dni, "solicitud", {
              ...fieldsFor(documentConfigs.solicitud, solicitudValues),
              entry_level: values.entry_level,
            }),
            saveEnrollmentDocument(dni, "anexo", fieldsFor(documentConfigs.anexo, anexoValues)),
          ]);
          setStatus("saved");
        } catch (saveError) {
          console.error(saveError);
          setStatus("save-error");
        }
      }

      if (exportDocuments) {
        await exportDocuments(type, { solicitud: solicitudValues, anexo: anexoValues });
        return;
      }

      const solicitud = type !== "anexo" ? await buildPdf("solicitud") : null;
      const anexo = type !== "solicitud" ? await buildPdf("anexo") : null;
      let bytes = solicitud || anexo;
      if (type === "complete") {
        const { mergePdfDocuments } = await import("../../utils/generateCompletedDocumentPdf");
        bytes = await mergePdfDocuments(solicitud, anexo);
      }
      const blobUrl = URL.createObjectURL(new Blob([bytes], { type: "application/pdf" }));
      const link = window.document.createElement("a");
      link.href = blobUrl;
      link.download =
        type === "complete"
          ? `Documentacion-completa-ISC-${dni || ENROLLMENT_YEAR}.pdf`
          : type === "solicitud"
          ? documentConfigs.solicitud.completedPdfName
          : documentConfigs.anexo.completedPdfName;
      window.document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
    } catch (downloadError) {
      console.error(downloadError);
      setError("No se pudo generar la documentación. Intentá nuevamente.");
    } finally {
      setBusy("");
    }
  };

  return (
    <main
      className={`document-editor documentacion-completa${
        versionLabel === "V2" ? " document-editor--v2" : ""
      }`}
    >
      <header className="document-editor__toolbar">
        <div className="document-editor__toolbar-intro">
          <Link className="document-editor__back" to={backHref}>
            ← Volver a los documentos
          </Link>
          <p className="document-editor__eyebrow">
            Inscripción {ENROLLMENT_YEAR} · Formulario único
          </p>
          <h1>Documentación completa</h1>
          <p>
            Completá una sola vez la Solicitud y el Anexo. Después podés descargarlos juntos o por
            separado.
          </p>
        </div>
      </header>

      {error && (
        <p className="document-editor__error" role="alert">
          {error}
        </p>
      )}
      {status && (
        <p
          className={`document-editor__save-status${
            status === "save-error" ? " document-editor__save-status--error" : ""
          }`}
          role="status"
        >
          {status === "loaded" && "Recuperamos los datos guardados para este DNI."}
          {status === "not-found" &&
            "No hay datos guardados para este DNI. Podés completar el formulario."}
          {status === "saved" && "Datos guardados."}
          {status === "save-error" && "Los PDF se descargarán, pero no pudimos guardar los datos."}
        </p>
      )}

      <div className="enrollment-fields__steps">
        <h2>{step === "form" ? "1. Completá los datos" : "2. Revisá y descargá"}</h2>
        <p>
          {step === "form"
            ? "Los datos compartidos se completan automáticamente en ambos documentos."
            : "Revisá cada documento antes de descargar la documentación completa."}
        </p>
      </div>

      {step === "form" ? (
        <form
          className="enrollment-fields"
          onSubmit={(event) => {
            event.preventDefault();
            setStep("preview");
          }}
        >
          <EnrollmentFields
            documentType="solicitud"
            fields={documentConfigs.solicitud.fields}
            values={values}
            onChange={updateField}
            onStudentDniBlur={loadByDni}
            disabled={Boolean(busy)}
            hiddenFieldIds={hiddenSolicitudFields}
          />
          <div className="documentacion-completa__divider">
            <span>Datos exclusivos del Anexo</span>
          </div>
          <EnrollmentFields
            documentType="anexo"
            fields={documentConfigs.anexo.fields}
            values={values}
            onChange={updateField}
            disabled={Boolean(busy)}
            hiddenFieldIds={hiddenAnexoFields}
            showAnexoAuthorizationInfo
          />
          <div className="enrollment-fields__actions">
            <Button type="submit" variant="contained" disabled={Boolean(busy)}>
              Continuar a la vista previa
            </Button>
          </div>
        </form>
      ) : (
        <>
          <div className="documentacion-completa__preview-actions">
            <Button variant="outlined" onClick={() => setStep("form")} disabled={Boolean(busy)}>
              Volver al formulario
            </Button>
            <div className="documentacion-completa__top-downloads">
              <Button
                variant="contained"
                disabled={Boolean(busy)}
                onClick={() => download("solicitud")}
                startIcon={
                  busy === "solicitud" ? <CircularProgress color="inherit" size={17} /> : null
                }
              >
                {busy === "solicitud" ? "Generando…" : "Descargar Solicitud"}
              </Button>
              <Button
                variant="contained"
                disabled={Boolean(busy)}
                onClick={() => download("anexo")}
                startIcon={busy === "anexo" ? <CircularProgress color="inherit" size={17} /> : null}
              >
                {busy === "anexo" ? "Generando…" : "Descargar Anexo"}
              </Button>
              <Button
                variant="contained"
                disabled={Boolean(busy)}
                onClick={() => download("complete")}
                startIcon={
                  busy === "complete" ? <CircularProgress color="inherit" size={17} /> : null
                }
              >
                {busy === "complete" ? "Generando…" : "Descargar documentación completa"}
              </Button>
            </div>
          </div>
          <aside className="documentacion-completa__print-note">
            <strong>Tenés que presentar los dos documentos.</strong>
            <p>Descargá, imprimí y firmá tanto la Solicitud de inscripción como el Anexo.</p>
          </aside>
          {[
            ["solicitud", solicitudValues],
            ["anexo", anexoValues],
          ].map(([type, documentValues]) => {
            const config = documentConfigs[type];
            return (
              <section
                className="documentacion-completa__document"
                key={type}
                aria-labelledby={`preview-${type}`}
              >
                <header className="documentacion-completa__document-heading">
                  <div>
                    <span>{type === "solicitud" ? "Documento 1 de 2" : "Documento 2 de 2"}</span>
                    <h3 id={`preview-${type}`}>{config.heading}</h3>
                  </div>
                </header>
                <div className="document-editor__viewport document-editor__viewport--preview">
                  {renderPreview
                    ? renderPreview({ documentType: type, values: documentValues })
                    : config.pages.map((page, pageIndex) => (
                        <DocumentPreviewPage
                          key={page.imageUrl}
                          page={page}
                          pageIndex={pageIndex}
                          fields={config.fields}
                          values={documentValues}
                          showValueBackground={type === "anexo"}
                        />
                      ))}
                </div>
              </section>
            );
          })}
        </>
      )}
    </main>
  );
}
